// client/src/pages/EmailLogPage.jsx
import { useState } from 'react'
import { Mail, ChevronLeft, ChevronRight, X, Eye, AlertCircle } from 'lucide-react'
import clsx from 'clsx'
import { useEmailLog, useEmailHtml } from '../api/hooks'
import { useAuth } from '../context/AuthContext'

// ─── Constants ────────────────────────────────────────────────────────────────

const TYPE_LABELS = {
  test_email:                'Test Email',
  admin_invited:             'Admin Invite',
  welcome:                   'Welcome',
  quote_sent:                'Quote Sent',
  quote_approved:            'Quote Approved',
  quote_rejected:            'Quote Declined',
  job_submitted:             'Job Submitted',
  job_accepted:              'Job Accepted',
  job_declined:              'Job Declined',
  job_cancelled:             'Job Cancelled',
  job_scheduled:             'Job Scheduled',
  job_completed:             'Job Completed',
  job_waiting_on_parts:      'Waiting on Parts',
  job_investigation:         'Investigation Required',
  invoice_sent:              'Invoice Sent',
  garage_request_approved:   'Registration Approved',
  garage_request_declined:   'Registration Declined',
  mot_reminder:              'MOT Reminder',
  checkin_reminder:          'Check-In Reminder',
}

const TYPE_STYLES = {
  test_email:                'bg-gray-500/15 text-gray-400',
  admin_invited:             'bg-violet-500/15 text-violet-400',
  welcome:                   'bg-purple-500/15 text-purple-400',
  quote_sent:                'bg-indigo-500/15 text-indigo-400',
  quote_approved:            'bg-green-500/15 text-green-400',
  quote_rejected:            'bg-red-500/15 text-red-400',
  job_submitted:             'bg-blue-500/15 text-blue-400',
  job_accepted:              'bg-teal-500/15 text-teal-400',
  job_declined:              'bg-red-500/15 text-red-400',
  job_cancelled:             'bg-red-500/15 text-red-400',
  job_scheduled:             'bg-indigo-500/15 text-indigo-400',
  job_completed:             'bg-green-500/15 text-green-400',
  job_waiting_on_parts:      'bg-orange-500/15 text-orange-400',
  job_investigation:         'bg-purple-500/15 text-purple-400',
  invoice_sent:              'bg-amber-500/15 text-amber-400',
  garage_request_approved:   'bg-emerald-500/15 text-emerald-400',
  garage_request_declined:   'bg-rose-500/15 text-rose-400',
  mot_reminder:              'bg-red-500/15 text-red-400',
  checkin_reminder:          'bg-cyan-500/15 text-cyan-400',
}

const STATUS_LABELS = {
  sent:       'Sent',
  delivered:  'Delivered',
  failed:     'Failed',
  bounced:    'Bounced',
  complained: 'Complained',
  opened:     'Opened',
  clicked:    'Clicked',
}

const STATUS_STYLES = {
  sent:       'bg-amber-500/15 text-amber-400',
  delivered:  'bg-green-500/15 text-green-400',
  failed:     'bg-red-500/15 text-red-400',
  bounced:    'bg-red-500/15 text-red-400',
  complained: 'bg-orange-500/15 text-orange-400',
  opened:     'bg-emerald-500/15 text-emerald-400',
  clicked:    'bg-teal-500/15 text-teal-400',
}

const PAGE_SIZE = 25

const fmtDateTime = (d) => d
  ? new Date(d).toLocaleString('en-GB', {
      day: 'numeric', month: 'short', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    })
  : '—'

const selectCls = `px-3 py-1.5 text-sm bg-[var(--bg-base)] border border-[var(--border)]
  rounded-lg text-[var(--text-primary)] focus:outline-none focus:border-indigo-500
  transition-colors cursor-pointer`

// ─── Email preview modal ──────────────────────────────────────────────────────

function EmailPreviewModal({ emailId, subject, onClose }) {
  const { data: html, isLoading, error } = useEmailHtml(emailId)

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70">
      <div className="w-full max-w-3xl bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl
                      flex flex-col shadow-2xl" style={{ height: '82vh' }}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-[var(--border)] shrink-0">
          <div className="min-w-0">
            <p className="text-xs text-[var(--text-muted)] mb-0.5">Email preview</p>
            <h2 className="font-medium text-[var(--text-primary)] truncate">{subject}</h2>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 text-[var(--text-muted)] hover:text-[var(--text-primary)] shrink-0 ml-4 transition-colors"
          >
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-hidden rounded-b-xl">
          {isLoading ? (
            <div className="flex items-center justify-center h-full text-[var(--text-muted)] text-sm">
              Loading preview…
            </div>
          ) : error || !html ? (
            <div className="flex flex-col items-center justify-center h-full gap-3 text-[var(--text-muted)]">
              <AlertCircle size={28} className="opacity-40" />
              <p className="text-sm">
                {error?.response?.data?.error || 'Preview not available for this email'}
              </p>
            </div>
          ) : (
            <iframe
              title="Email preview"
              srcDoc={html}
              sandbox="allow-same-origin"
              className="w-full h-full border-0 rounded-b-xl bg-white"
            />
          )}
        </div>
      </div>
    </div>
  )
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function EmailLogPage() {
  const { user } = useAuth()
  const isAdmin  = user?.role === 'platform_admin'
  const isGarage = user?.role === 'garage_user' || user?.role === 'garage_admin'

  const [typeFilter,   setTypeFilter]   = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [page,         setPage]         = useState(1)
  const [previewing,   setPreviewing]   = useState(null) // { id, subject }

  const { data, isLoading } = useEmailLog({
    ...(typeFilter   ? { type: typeFilter }     : {}),
    ...(statusFilter ? { status: statusFilter } : {}),
    page,
    limit: PAGE_SIZE,
  })

  const emails     = data?.emails ?? []
  const total      = data?.total  ?? 0
  const totalPages = Math.ceil(total / PAGE_SIZE)

  const handleFilter = (setter) => (e) => {
    setter(e.target.value)
    setPage(1)
  }

  return (
    <div className="max-w-5xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-xl font-semibold text-[var(--text-primary)]">Email Log</h1>
          <p className="text-sm text-[var(--text-secondary)] mt-0.5">
            {isAdmin  ? 'All emails sent by the platform' :
             isGarage ? 'Emails sent on behalf of your garage' :
             'Emails sent to your account'}
          </p>
        </div>

        {/* Filters */}
        <div className="flex items-center gap-2 flex-wrap">
          <select value={typeFilter} onChange={handleFilter(setTypeFilter)} className={selectCls}>
            <option value="">All types</option>
            {Object.entries(TYPE_LABELS).map(([v, l]) => (
              <option key={v} value={v}>{l}</option>
            ))}
          </select>
          <select value={statusFilter} onChange={handleFilter(setStatusFilter)} className={selectCls}>
            <option value="">All statuses</option>
            {Object.entries(STATUS_LABELS).map(([v, l]) => (
              <option key={v} value={v}>{l}</option>
            ))}
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="bg-[var(--bg-surface)] border border-[var(--border)] rounded-xl overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center py-16 text-[var(--text-muted)] text-sm">
            Loading…
          </div>
        ) : emails.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 gap-3 text-[var(--text-muted)]">
            <Mail size={32} className="opacity-30" />
            <p className="text-sm">No emails found</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-[var(--border)]">
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-[var(--text-muted)]">
                    Subject
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-[var(--text-muted)] hidden sm:table-cell">
                    Type
                  </th>
                  {(isAdmin || isGarage) && (
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-[var(--text-muted)] hidden md:table-cell">
                      Recipient
                    </th>
                  )}
                  {isAdmin && (
                    <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-[var(--text-muted)] hidden lg:table-cell">
                      Garage
                    </th>
                  )}
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-[var(--text-muted)]">
                    Status
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wider text-[var(--text-muted)] hidden sm:table-cell">
                    Sent
                  </th>
                  <th className="px-4 py-3 w-10" />
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--border)]">
                {emails.map(email => (
                  <tr key={email.id} className="hover:bg-[var(--bg-overlay)] transition-colors group">
                    <td className="px-4 py-3 text-[var(--text-primary)] font-medium max-w-xs truncate">
                      {email.subject}
                    </td>
                    <td className="px-4 py-3 hidden sm:table-cell">
                      <span className={clsx(
                        'px-2 py-0.5 text-xs font-medium rounded-full whitespace-nowrap',
                        TYPE_STYLES[email.type] || 'bg-gray-500/15 text-gray-400'
                      )}>
                        {TYPE_LABELS[email.type] || email.type}
                      </span>
                    </td>
                    {(isAdmin || isGarage) && (
                      <td className="px-4 py-3 text-[var(--text-secondary)] hidden md:table-cell">
                        <div className="truncate max-w-[180px]">
                          {email.to_name
                            ? <><span className="text-[var(--text-primary)]">{email.to_name}</span><br /><span className="text-xs text-[var(--text-muted)]">{email.to_email}</span></>
                            : email.to_email}
                        </div>
                      </td>
                    )}
                    {isAdmin && (
                      <td className="px-4 py-3 text-[var(--text-secondary)] hidden lg:table-cell text-xs">
                        {email.garage_name || '—'}
                      </td>
                    )}
                    <td className="px-4 py-3">
                      <span className={clsx(
                        'px-2 py-0.5 text-xs font-medium rounded-full whitespace-nowrap',
                        STATUS_STYLES[email.status] || 'bg-gray-500/15 text-gray-400'
                      )}>
                        {STATUS_LABELS[email.status] || email.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-[var(--text-muted)] text-xs hidden sm:table-cell whitespace-nowrap">
                      {fmtDateTime(email.created_at)}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => setPreviewing({ id: email.id, subject: email.subject })}
                        title="Preview email"
                        className="p-1.5 text-[var(--text-muted)] hover:text-indigo-400
                                   transition-all rounded-lg hover:bg-[var(--bg-elevated)]"
                      >
                        <Eye size={15} />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between text-sm text-[var(--text-secondary)]">
          <span>
            {((page - 1) * PAGE_SIZE) + 1}–{Math.min(page * PAGE_SIZE, total)} of {total}
          </span>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
              className="p-1.5 rounded-lg hover:bg-[var(--bg-overlay)] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronLeft size={16} />
            </button>
            <span className="px-2 text-xs text-[var(--text-muted)]">
              {page} / {totalPages}
            </span>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="p-1.5 rounded-lg hover:bg-[var(--bg-overlay)] disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}

      {/* Preview modal */}
      {previewing && (
        <EmailPreviewModal
          emailId={previewing.id}
          subject={previewing.subject}
          onClose={() => setPreviewing(null)}
        />
      )}
    </div>
  )
}
